import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import marvelMtg from '../assets/images/marvel_mtg.png';
import warhammer40k from '../assets/images/warhammer_40k.png';

// Public S3 bucket managed by the tcg-square-automation admin tool
// ("Homepage Banners" tab). banners.json holds the slide list in display order.
const BANNERS_BUCKET_URL =
  process.env.REACT_APP_BANNERS_BUCKET_URL ||
  'https://nlg-banners.s3.us-east-1.amazonaws.com';

// Gradient palette offered by the admin tool. Keeping the class strings as
// literals here also ensures Tailwind's content scan includes them in the build.
const BANNER_GRADIENTS = [
  'from-red-600 to-yellow-500',
  'from-gray-700 to-red-800',
  'from-purple-600 to-pink-600',
  'from-blue-600 to-cyan-400',
  'from-green-600 to-teal-400',
  'from-orange-500 to-red-600',
  'from-indigo-600 to-purple-600',
  'from-slate-800 to-blue-900',
];

// Shown until banners.json loads, and kept if it's unavailable or empty
const FALLBACK_SLIDES = [
  {
    title: 'Marvel Super Heroes',
    subtitle: 'Magic: The Gathering',
    description: 'The Marvel Universe comes to Magic — now in stock!',
    cta: 'Shop Now',
    ctaLink: '/shop/tradingcardgames?category_id=HXF5G7IVLCXNJDQQQNVZBCW6',
    gradient: 'from-red-600 to-yellow-500',
    image: marvelMtg,
    isImageFile: true,
  },
  {
    title: 'Warhammer 40,000',
    subtitle: 'Miniature Wargame',
    description: 'Build Your Army. Conquer The Battlefield.',
    cta: 'Shop Now',
    ctaLink: '/shop/tabletopgames?category_id=AENFPWJBS745UDMTDNHVWF7S',
    gradient: 'from-gray-700 to-red-800',
    image: warhammer40k,
    isImageFile: true,
  },
  {
    title: 'Weekly Tournaments',
    subtitle: 'Check out our lineup',
    description: 'Join us for competitive play, prizes, and community fun',
    cta: 'View Events',
    ctaLink: '/events',
    gradient: 'from-purple-600 to-pink-600',
    image: null,
    isImageFile: false,
  },
];

const bannerToSlide = (banner) => ({
  title: banner.title,
  subtitle: banner.subtitle,
  description: banner.description,
  cta: banner.cta,
  ctaLink: banner.category_id
    ? `${banner.route}?category_id=${banner.category_id}`
    : banner.route,
  gradient: BANNER_GRADIENTS.includes(banner.gradient)
    ? banner.gradient
    : BANNER_GRADIENTS[0],
  image: banner.image_key ? `${BANNERS_BUCKET_URL}/${banner.image_key}` : null,
  isImageFile: !!banner.image_key,
});

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState(FALLBACK_SLIDES);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BANNERS_BUCKET_URL}/banners.json`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((banners) => {
        if (!cancelled && Array.isArray(banners) && banners.length > 0) {
          setSlides(banners.map(bannerToSlide));
          setCurrentSlide(0);
        }
      })
      .catch(() => {
        // banners.json missing or unreachable — keep the fallback slides
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000);
  }, [slides.length]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    startTimer();
  };
  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    startTimer();
  };

  return (
    <section className="relative h-[600px] lg:h-[700px] flex items-center overflow-hidden pt-20">
      {/* Prev / Next Arrows */}
      <button
        onClick={goToPrev}
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
      </button>

      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
      
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundPosition: 'center'
        }}></div>
      </div>

      <div className="container relative z-10 px-14 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.5 }}
            >
              {/* <div className="flex items-center mb-6">
                <Sparkles className="w-6 h-6 text-yellow-400 mr-2" />
                <span className="text-yellow-400 font-semibold">Limited Time Offers</span>
              </div> */}
              
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">
                {slides[currentSlide].title}
              </h1>
              
              <p className={`text-2xl lg:text-3xl font-semibold bg-gradient-to-r ${slides[currentSlide].gradient} bg-clip-text text-transparent mb-6`}>
                {slides[currentSlide].subtitle}
              </p>
              
              <p className="text-gray-300 text-lg mb-8 max-w-lg">
                {slides[currentSlide].description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={slides[currentSlide].ctaLink}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 transform hover:scale-105 shadow-xl"
                >
                  {slides[currentSlide].cta}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                {/* <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur text-white font-semibold rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-200"
                >
                  Learn More
                </Link> */}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Visual Element */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.8, rotate: 10 }}
              transition={{ duration: 0.5 }}
              className="hidden lg:flex justify-center items-center h-[460px]"
            >
              <div className="relative flex max-w-full max-h-full">
                <div className={`absolute inset-0 bg-gradient-to-br ${slides[currentSlide].gradient} blur-3xl opacity-50`}></div>
                {slides[currentSlide].isImageFile ? (
                  <img 
                    src={slides[currentSlide].image} 
                    alt={slides[currentSlide].title}
                    className="relative w-auto h-auto max-w-lg max-h-[460px] object-contain"
                  />
                ) : (
                  <div className="relative text-[200px] animate-pulse">
                    {slides[currentSlide].image}
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'w-8 bg-primary-500'
                : 'w-2 bg-white/30 hover:bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
